import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';

import { 
  Building2, MapPin, Globe, Mail, Phone, Calendar, Users,
  Heart, Share2, ExternalLink, Loader2, CheckCircle, Instagram,
  Youtube, Twitter, Facebook
} from 'lucide-react';


export default function OrganizationProfilePage() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const [org, setOrg] = useState(null);
  const [events, setEvents] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [activeTab, setActiveTab] = useState('about');

  useEffect(() => {
    fetchOrganization();
  }, [handle]);

  useEffect(() => {
    if (org) {
      fetchEvents();
      fetchPosts();
    }
  }, [org]);

  const fetchOrganization = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/organizations/${handle}`);
      if (res.ok) {
        const data = await res.json();
        setOrg(data);
        setIsFollowing(data.is_following || false);
      } else {
        setOrg(null);
      }
    } catch (err) {
      console.error('Failed to fetch organization:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await fetch(`${API}/organizations/${org.org_id}/events`);
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch (err) {
      console.error('Failed to fetch events:', err);
    }
  };

  const fetchPosts = async () => {
    try {
      const res = await fetch(`${API}/organizations/${org.org_id}/posts`);
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts || []);
      }
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    }
  };

  const handleFollow = async () => {
    if (!isAuthenticated) {
      toast.info('Sign in to follow organizations');
      return;
    }
    
    try {
      const endpoint = isFollowing ? 'unfollow' : 'follow';
      const res = await fetch(`${API}/organizations/${org.org_id}/${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        setIsFollowing(!isFollowing);
        toast.success(isFollowing ? 'Unfollowed' : 'Following!');
      }
    } catch (err) {
      toast.error('Failed to update follow');
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: org.name, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied!');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(shareUrl);
          toast.success('Link copied!');
        } catch {}
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Organization Not Found</h1>
          <p className="text-muted-foreground mb-4">This organization doesn't exist or has been removed.</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" data-testid="org-profile-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="glass-card border-border rounded-xl p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Logo */}
            <div className="w-32 h-32 rounded-xl bg-muted/50 overflow-hidden flex-shrink-0">
              {org.profile_image_url ? (
                <img src={org.profile_image_url} alt={org.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Building2 className="w-16 h-16 text-muted-foreground" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="font-heading text-2xl md:text-3xl font-bold">{org.name}</h1>
                    {org.verified && (
                      <CheckCircle className="w-6 h-6 text-secondary fill-secondary/20" />
                    )}
                  </div>
                  <p className="text-muted-foreground">{org.handle ? `@${org.handle}` : ""}</p>
                </div>
                <Badge variant="outline" className="capitalize">
                  {org.org_type?.replace('_', ' ')}
                </Badge>
              </div>

              {/* Location */}
              <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span>
                  {[org.neighborhood, org.city, org.state].filter(Boolean).join(', ')}
                </span>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-3 mt-4">
                <Button 
                  onClick={handleFollow}
                  variant={isFollowing ? "outline" : "default"}
                  data-testid="follow-org-btn"
                >
                  <Heart className={`w-4 h-4 mr-2 ${isFollowing ? 'fill-current' : ''}`} />
                  {isFollowing ? 'Following' : 'Follow'}
                </Button>
                <Button variant="outline" onClick={handleShare}>
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
                {org.website && (
                  <a href={org.website} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline">
                      <Globe className="w-4 h-4 mr-2" />
                      Website
                    </Button>
                  </a>
                )}
                {org.donate_url && (
                  <a href={org.donate_url} target="_blank" rel="noopener noreferrer">
                    <Button className="bg-green-600 hover:bg-green-700">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Donate / Support
                    </Button>
                  </a>
                )}
                {org.volunteer_url && (
                  <a href={org.volunteer_url} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="text-purple-500 border-purple-500/50 hover:bg-purple-500/10">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Volunteer
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start mb-6">
            <TabsTrigger value="about" data-testid="tab-about">About</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
            <TabsTrigger value="posts" data-testid="tab-posts">Posts</TabsTrigger>
          </TabsList>

          {/* About Tab */}
          <TabsContent value="about">
            <Card className="glass-card border-border p-6">
              <h2 className="font-heading text-xl font-bold mb-4">Mission</h2>
              <p className="text-muted-foreground whitespace-pre-wrap">{org.mission}</p>

              {/* Contact Info */}
              <div className="mt-6 pt-6 border-t border-border">
                <h3 className="font-semibold mb-4">Contact</h3>
                <div className="space-y-2">
                  {org.contact_email && (
                    <a href={`mailto:${org.contact_email}`} className="flex items-center gap-2 text-secondary hover:underline">
                      <Mail className="w-4 h-4" />
                      {org.contact_email}
                    </a>
                  )}
                  {org.contact_phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="w-4 h-4" />
                      {org.contact_phone}
                    </div>
                  )}
                  {org.website && (
                    <a href={org.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-secondary hover:underline">
                      <Globe className="w-4 h-4" />
                      {org.website}
                    </a>
                  )}
                </div>
              </div>

              {/* Social Links */}
              {org.socials && Object.keys(org.socials).length > 0 && (
                <div className="mt-6 pt-6 border-t border-border">
                  <h3 className="font-semibold mb-4">Social Media</h3>
                  <div className="flex gap-3">
                    {org.socials.instagram && (
                      <a href={org.socials.instagram} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-muted/30 hover:bg-muted/50">
                        <Instagram className="w-5 h-5" />
                      </a>
                    )}
                    {org.socials.youtube && (
                      <a href={org.socials.youtube} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-muted/30 hover:bg-muted/50">
                        <Youtube className="w-5 h-5" />
                      </a>
                    )}
                    {org.socials.twitter && (
                      <a href={org.socials.twitter} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-muted/30 hover:bg-muted/50">
                        <Twitter className="w-5 h-5" />
                      </a>
                    )}
                    {org.socials.facebook && (
                      <a href={org.socials.facebook} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-muted/30 hover:bg-muted/50">
                        <Facebook className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            {events.length > 0 ? (
              <div className="grid gap-4">
                {events.map(event => (
                  <Card key={event.event_id} className="glass-card border-border p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-heading font-bold">{event.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{event.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(event.event_date).toLocaleDateString()}
                          </div>
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {event.neighborhood}
                          </div>
                        </div>
                      </div>
                      <Link to={`/events/${event.event_id}`}>
                        <Button size="sm">View</Button>
                      </Link>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="glass-card border-border p-8 text-center">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading font-bold mb-2">No Events Yet</h3>
                <p className="text-muted-foreground">This organization hasn't posted any events.</p>
              </Card>
            )}
          </TabsContent>

          {/* Posts Tab */}
          <TabsContent value="posts">
            {posts.length > 0 ? (
              <div className="space-y-4">
                {posts.map(post => (
                  <Card key={post.post_id} className="glass-card border-border p-4">
                    <p className="whitespace-pre-wrap">{post.content_text}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(post.created_at).toLocaleDateString()}
                    </p>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="glass-card border-border p-8 text-center">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading font-bold mb-2">No Posts Yet</h3>
                <p className="text-muted-foreground">This organization hasn't posted anything yet.</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
